import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import { useDebounce } from '../Utils/useDounce';
import { useCategoryListParams } from './useCategoryListParams';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import { Add } from '@mui/icons-material';
import { useBlockOrUnblockStoreCategoryById, useDeleteStoreCategoryById, useGetListCategories } from '../../Query/AddStoreCategoryQuery';

type Category = {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  isActive: boolean;
  addedBy: string;
  state: boolean;
  createdUserName: string;
  categoryImage?: string;
  categoryIcon?: string;
};

type CategoryListProps = {
  editCategory: (category: Category) => void;
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit'>>;
  addCategory: () => void;
};

export const categoryStoreAtom = atomWithStorage<string | null>('categoryStoreAtom', null);

const CategoryList: React.FC<CategoryListProps> = ({
  editCategory,
  setMode,
  addCategory,
}) => {
  const setCategory = useSetAtom(categoryStoreAtom);
  console.log(setCategory);
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('info');

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useCategoryListParams();
  const { data: categoryList, isLoading, refetch } = useGetListCategories(params);



  const deleteCategoryMutation = useDeleteStoreCategoryById();
  const { mutateAsync: blockStoreCategoryAsync } =
    useBlockOrUnblockStoreCategoryById();

  // Create filtered rows from categoryList data
  const filteredRows = useMemo(() => {
    if (!categoryList?.data) return [];
    return categoryList.data.map((category: any, index: number) => ({
      ...category,
      serialNumber: (params.page - 1) * params.limit + index + 1,
    }));
  }, [categoryList?.data, params.page, params.limit]);


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch, dispatch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleEditCategory = (category: Category) => {
    setMode('edit');
    editCategory(category);
  };

  const handleBlock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No category selected to block', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to block ${selectedCategoryIds.length} Category(s)?`
      )
    ) {
      try {

        await blockStoreCategoryAsync({
          id: selectedCategoryIds,
          data: { status: 'blocked' },
        });
        showMessage('Category blocked successfully', 'success');
        refetch();
      } catch (error) {
        console.error('Error in handleBlock:', error);
        showMessage('Failed to block categories', 'error');
      }
    }
  };

  const handleUnblock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No category selected to unblock', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to unblock ${selectedCategoryIds.length} Category(s)?`
      )
    ) {
      try {
        await blockStoreCategoryAsync({
          id: selectedCategoryIds,
          data: { status: 'active' },
        });

        showMessage('Category unblocked successfully', 'success');
        refetch();
      } catch (error) {
        console.error('Error in handleUnblock:', error);
        showMessage('Failed to unblock categories', 'error');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        deleteCategoryMutation.mutate(id, {
          onSuccess: () => {
            refetch();
            setSelectedCategoryIds([]);
            showMessage('Category deleted successfully', 'success');
          },
          onError: (error: any) => {
            showMessage(error?.message || 'Failed to delete category', 'error');
          },
        });
      } catch (error: any) {
        showMessage(error?.message || 'Failed to delete category', 'error');
      }
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No categories selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCategoryIds.length} category(s)?`
      )
    ) {
      // Delete categories one by one since the API only supports single deletion
      const deletePromises = selectedCategoryIds.map(id =>
        deleteCategoryMutation.mutateAsync(id)
      );

      Promise.all(deletePromises)
        .then(() => {
          showMessage(
            `${selectedCategoryIds.length} category(s) deleted successfully`,
            'success'
          );
          setSelectedCategoryIds([]);
          refetch();
        })
        .catch((error: any) => {
          showMessage(error?.message || 'Failed to delete some categories', 'error');
        });
    }
  };

  // Determine bulk button visibility based on selection statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = categoryList?.data || [];
    const idToActive = new Map<string, boolean>(
      dataList.map((item: any) => [String(item.id), Boolean(item.isActive)])
    );
    const statuses = selectedCategoryIds.map((id) => idToActive.get(String(id)));
    const hasActive = statuses.some((s) => s === true);
    const hasInactive = statuses.some((s) => s === false);
    return { showBlock: hasActive, showUnblock: hasInactive };
  }, [selectedCategoryIds, categoryList]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('storeCategory', 'Edit');
    const canDelete = hasPermission('storeCategory', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDelete(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns = [
    {
      field: 'serialNumber',
      headerName: 'S.No',
      width: 90,
      disableColumnMenu: true,
      sortable: true,
    },
    { field: 'nameEnglish', headerName: 'Category Name', flex: 1 },
    { field: 'storeType', headerName: 'Store Type', flex: 1 },
    {
      field: 'createdUserType',
      headerName: 'Added By',
      width: 120,
      disableColumnMenu: true,
      sortable: false,
    },
    // Conditionally include status column only if user has Block or Unblock permission
     ...(hasPermission('storeCategory', 'Block') || hasPermission('storeCategory', 'Unblock') ? [{
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isActive = params.row.isActive;
        const isBlocked = !isActive;
        const action = isBlocked ? 'Unblock' : 'Block';


        // Check if user has permission for the specific action
        if (!hasPermission('storeCategory', action)) {
          return null;
        }

        const toggleStatus = async () => {
          const newStatus = isBlocked ? 'active' : 'blocked';
          const actionText = isBlocked ? 'unblock' : 'block';

          const confirmed = window.confirm(
            `Are you sure you want to ${actionText} category?`
          );
          if (!confirmed) return;

          try {
            await blockStoreCategoryAsync({
              id: [params.row.id],
              data: { status: newStatus },
            });

            showMessage(
              `Category ${newStatus === 'active' ? 'unblocked' : 'blocked'} successfully`,
              'success'
            );

            refetch();
          } catch (error) {
            console.error(error);
            showMessage('Failed to update category status', 'error');
          }
        };

        return (
          <Tooltip title={isActive ? 'Active' : 'Blocked'}>
            <IconButton size="small" onClick={toggleStatus}>
              {isActive ? (
                <CheckCircleIcon
                  color="success"
                  fontSize="small"
                />
              ) : (
                <BlockIcon
                  color="error"
                  fontSize="small"
                />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    }] : []),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => <RowActions row={params.row} />,
    },
  ];

  const handleCategorySelection = (ids: string[]) => {
    setSelectedCategoryIds(ids);
  };

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={snackbarSeverity}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            size="medium"
            placeholder="Search by Store Category Name"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none', // remove inner border
              },
              '& fieldset': {
                border: 'none', // remove Material-UI default border
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px', // adjust this value to move the placeholder lower
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          alignItems="center"
        >
          <Grid>
            <PermissionActionButtons
              module="storeCategory"
              onBlock={showBlock ? handleBlock : undefined}
              onUnblock={showUnblock ? handleUnblock : undefined}
              onDelete={selectedCategoryIds.length ? handleDeleteSelected : undefined}
            />
          </Grid>
          <Grid>
            {hasPermission('storeCategory', 'Add') && (
              <Button
                onClick={() => addCategory()}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                startIcon={<Add sx={{ fontSize: 16}} />}
              >
                Add Category
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <CommonDataGrid
        key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows || []}
        columns={columns}
        pageSize={params.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection
        loading={isLoading}
        page={params.page - 1}
        rowCount={categoryList?.pagination?.totalItems}
        selectionModel={selectedCategoryIds}
        onRowSelectionModelChange={handleCategorySelection}
        components={{
          NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
        }}
      />
    </Box>
  );
};

export default CategoryList;

